import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import {
	Checkbox,
	FormControlLabel,
	Typography,
	Button,
	Grid,
} from "@material-ui/core";

// NPM
import { useSpring, animated, config } from "react-spring";

// Utility
import Utility from "../domain/classes/Utility";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import TermsAndConditions from "../components/termsAndConditions/TermsAndConditions.Component";
import LoadingButton from "../components/common/LoadingButton.Component";
import GenericDialog from "../components/common/GenericDialog.Component";

// Contexts
import { useApplicationManager } from "../contexts/ApplicationManager.Context";

// Assets
import Journey from "../domain/classes/Journey";

// S3 Assets
import { ROOT_ID, SAD_COLOURED } from "../domain/utility/config";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		disagreeBtn: { margin: "auto" },
		disagreeBtnContainer: { display: "flex" },
		imgContainer: {
			width: 120,
			height: 120,
			display: "block",
			margin: "auto",
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(2),
		},
		img: {
			height: "100%",
		},
	});

export interface TermsAndConditionsSectionProps {}

const BUTTONS_CONTAINER_ID = "action-buttons-container";

const TermsAndConditionsSection = React.forwardRef<
	HTMLDivElement,
	TermsAndConditionsSectionProps & WithStyles<typeof styles>
>(({ classes }, ref) => {
	// Contexts
	const {
		clearApplicationData,
		clearCookies,
		handleJourneyStageChange,
		journeyStage,
	} = useApplicationManager();

	// States
	const [agreed, setAgreed] = React.useState<boolean>(false);
	const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);

	// Handlers
	const toggleAgreed = () => {
		setAgreed((prevState: boolean) => !prevState);
	};

	React.useEffect(() => {
		if (agreed) {
			Utility.scrollToTopOfElement(BUTTONS_CONTAINER_ID, -200);
		} else {
			setY({ y: 0 });
		}
	}, [agreed]);

	const [y, setY] = useSpring(() => {
		return {
			immediate: false,
			y: 0,
			onFrame: (props: any) => {
				window.scroll(0, props.y);
			},
			config: config.gentle,
		};
	});

	// Handlers
	const handleAgreeButtonClick = () => {
		handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		Utility.scrollToTopOfElement(ROOT_ID);
	};

	const handleDialogConfirm = () => {
		clearApplicationData();
		clearCookies();
		Utility.scrollToTopOfElement(ROOT_ID);
	};

	const handleDialogClose = () => {
		setDialogOpen(false);
	};

	const handleDisagreeButtonClick = () => {
		setDialogOpen(true);
		Utility.scrollToTopOfElement(ROOT_ID);
	};

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="md"
		>
			<animated.div>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography
							variant="h3"
							color="primary"
							component="h1"
							paragraph
							align="center"
						>
							QV Medical Online Appointment Booking Service
						</Typography>
						<Typography
							variant="caption"
							color="primary"
							component="span"
							paragraph
							align="center"
							display="block"
						>
							powered by Practiq
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography
							variant="h4"
							color="primary"
							component="h3"
							paragraph
							align="center"
						>
							Practiq Terms of Service
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Checkbox
									checked={agreed}
									color="primary"
									name="Agreed to terms and conditions"
									onChange={toggleAgreed}
									size="small"
								/>
							}
							label={
								<Typography variant="body2" color="primary">
									<strong>
										I have read and understood the terms and
										conditions.
									</strong>
								</Typography>
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<TermsAndConditions />
					</Grid>
				</Grid>
				<Grid
					container
					spacing={3}
					style={{ marginTop: 8 }}
					id={BUTTONS_CONTAINER_ID}
				>
					<Grid item xs={12}>
						<LoadingButton
							fullWidth
							isLoading={false}
							onClick={handleAgreeButtonClick}
						>
							I agree
						</LoadingButton>
					</Grid>
					<Grid item xs={12} className={classes.disagreeBtnContainer}>
						<Button
							className={classes.disagreeBtn}
							onClick={handleDisagreeButtonClick}
						>
							I disagree
						</Button>
					</Grid>
				</Grid>
			</animated.div>
			<GenericDialog
				open={dialogOpen}
				title="Are you sure?"
				content={
					<React.Fragment>
						<div className={classes.imgContainer}>
							<img
								className={classes.img}
								src={SAD_COLOURED}
								alt="Are you sure you want to cancel?"
							/>
						</div>
						<Typography variant="body1" align="center" paragraph>
							All provided information will be lost and you will
							have to start again.
						</Typography>
					</React.Fragment>
				}
				actions={
					<React.Fragment>
						<Button onClick={handleDialogConfirm}>Yes</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleDialogClose}
						>
							No
						</Button>
					</React.Fragment>
				}
			/>
		</StyledSectionContainer>
	);
});

const StyledTermsAndConditionsSection = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("TermsAndConditionsSection"),
})(TermsAndConditionsSection);

export default StyledTermsAndConditionsSection;

export const AnimatedTermsAndConditionsSection = animated(
	StyledTermsAndConditionsSection
);
